import React, { useState, useEffect, useMemo, useRef, use } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import ManageCashModal from '../../components/PortfolioV2/ManageCashModal';
// --- Imports API V2 และ Context ---
import { getV2TransactionById, patchV2Transaction, getV2PortfolioDetail, getPublicV2PortfolioDetail, getPortfolioDetailTwo, updateStockInSubPortfolioTwo, deleteStockFromSubPortfolioTwo, requestRebuildPortfolio } from '../../composables/fetchAPI';
import { useMarketPrices } from '../../context/MarketPriceContext';
import { useExchangeRates } from '../../context/ExchangeRateContext';
import { usePortfolio } from '../../context/PortfolioContext';
import CSVImportModal from '../../components/PortfolioV2/CSVImportModal';
import CryptoCSVImportModal from '../../components/PortfolioV2/CryptoCSVImportModal';
// --- Imports Components ของคุณ ---
import PortfolioItem from '../../components/PortfolioItem';
import PortfolioStocksBox from '../../components/PortfolioV2/PortfolioStocksBox';
import PortfolioChartBox from '../../components/PortfolioV2/PortfolioChartBox';
import PortfolioSingleChartBox from '../../components/PortfolioSingleChartBox';
import BenchmarkComponent from '../../components/BenchmarkComponent';
import ToggleCurrency from '../../components/ToggleCurrency';
import AddSubPortV2Modal from '../../components/PortfolioV2/AddSubPortV2Modal';
import EditAllocationModal from '../../components/PortfolioV2/EditAllocationModal';
import MoveHoldingModal from '../../components/PortfolioV2/MoveHoldingModal';
import TransactionHistoryV2 from '../../components/PortfolioV2/TransactionHistoryV2';
import AddEditStock from '../../components/AddEditStock';
import ConfigTargetRatioStockPopup from '../../components/ConfigTargetRatioStockPopup';
import LoadingStock from '../../components/LoadingStock';
import EditHoldingAllocationModal from '../../components/PortfolioV2/EditHoldingAllocationModal';
import { getProgressBarColor } from '../../composables/colorUtils';
import ToggleSwitch from '../../components/ToggleSwitch'
import { isCryptoPortfolio, normalizeCryptoPortfolio } from '../../utils/portfolioType';
import { num } from '../../utils/cryptoDecimal';

// --- Helper function สำหรับคำนวณมูลค่ารวม (ใช้ UI เดิม) ---
function getPortfolioValue(portfolio: any, currentPrices: { [symbol: string]: number }) {
  const items = portfolio.holdings || portfolio.stocks || [];
  if (items.length === 0) return 0;
  return items.reduce((sum: number, stock: any) => {
    // stockSymbol ก่อน: holding คริปโตถูกปรับเป็น X:...USD ไว้ (ดู normalizeCryptoPortfolio)
    const symbol = stock.stockSymbol || stock.assetSymbol || stock.symbol;
    // ใช้ราคา Real-time ถ้าดึงไม่ทันให้ใช้ต้นทุนแทน
    const price = currentPrices[symbol] ?? num(stock.avgEntryPrice ?? stock.avgBuyPrice);
    return sum + num(stock.quantity) * price;
  }, 0);
}

// ---------------------------------------------------------------------------
// 1. Component "กล่องหน้าแรกของพอร์ต" (PortfolioAllocationBox)
// ---------------------------------------------------------------------------
const PortfolioAllocationBox = ({ subPortfolios, fullPortfolio, currentPrices, onSelectPortfolio, onOpenAllocation, onOpenManageCash, onRefresh, onEdit, ids, isPortAdmin, currency, convertCurrency, isCrypto = false }: any) => {
  const portfolioArray = Array.isArray(subPortfolios) ? subPortfolios : [];
  const navigate = useNavigate();
  // ✅ 1. ดึงข้อมูลเงินสดจาก summary แทนเพื่อประหยัด CPU
  const cashExpectedRatio = fullPortfolio?.cashExpectedRatio || 0;
  // พอร์ตคริปโตส่งเป็น decimal string ต้อง num() ก่อนบวก (แสดงผลเท่านั้น)
  const cashAmountUSD = num(fullPortfolio?.summary?.cashValue ?? fullPortfolio?.cashBalances?.[0]?.amount);
  const cashAmountConverted = convertCurrency ? convertCurrency(cashAmountUSD, currency) : cashAmountUSD;

  // 2. คำนวณ Ratio รวม
  const totalSubportRatio = portfolioArray.reduce((sum, portfolio) => sum + (portfolio.expectedRatio || 0), 0);
  const totalRatio = totalSubportRatio + cashExpectedRatio;

  // 3. คำนวณ Value รวม
  const portfolioValues = portfolioArray.map((p: any) => getPortfolioValue(p, currentPrices));
  const totalSubportValue = portfolioValues.reduce((sum, v) => sum + v, 0);
  const totalValue = totalSubportValue + cashAmountUSD;
  const cashActualRatio = totalValue > 0 ? (cashAmountUSD / totalValue) * 100 : 0;
  const targetCashAmount = totalValue * (cashExpectedRatio / 100);
  const cashDifference = targetCashAmount - cashAmountUSD;
  const cashDifferenceConverted = convertCurrency ? convertCurrency(cashDifference, currency) : cashDifference;
  const { displayMode } = usePortfolio();

  const progressPercentage = cashExpectedRatio > 0 ? (cashActualRatio / cashExpectedRatio) * 100 : 0;
  const progressBarWidth = Math.min(progressPercentage, 100);
  // 1. เพิ่ม state ควบคุมการรวมเงินสด (ค่าเริ่มต้นเป็น true หรือ false ตามชอบ)
  // ✅ บันทึกและดึงค่าสถานะการรวมเงินสดจาก localStorage (แยกตามพอร์ตด้วย id เพื่อไม่ให้ตีกันถ้ามีหลายพอร์ต)
  const STORAGE_KEY_INCLUDE_CASH = `portfolio_include_cash`;
  const [includeCashInRatio, setIncludeCashInRatio] = useState<boolean>(() => {
    const saved = localStorage.getItem(STORAGE_KEY_INCLUDE_CASH);
    return saved !== null ? JSON.parse(saved) : true; // ค่าเริ่มต้นเป็น true
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_INCLUDE_CASH, JSON.stringify(includeCashInRatio));
  }, [includeCashInRatio, STORAGE_KEY_INCLUDE_CASH]);

  // 2. คำนวณฐานของมูลค่ารวม (Total Value Base) ที่จะนำมาหารสัดส่วน
  // ถ้าเปิด toggle จะรวมเงินสดเข้าไปในฐานคำนวณสัดส่วน ถ้าปิดจะใช้เฉพาะมูลค่าหุ้นย่อย
  const ratioBaseValue = includeCashInRatio ? (totalSubportValue + cashAmountUSD) : totalSubportValue;
  return (
    <div className="rounded-b-xl shadow-lg p-1 h-full flex flex-col" style={{ backgroundColor: 'var(--component-bg-color)' }}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">        {/* ✅ รวมปุ่มและหัวข้อไว้ในกลุ่มเดียวกัน */}
        <div className=" flex items-center gap-3">
          <button
            onClick={() => navigate(isPortAdmin ? '/admin' : '/portfolio')}
            className="hidden md:block px-4 py-2 rounded hover:bg-gray-600 transition-colors items-center gap-2 whitespace-nowrap"
            style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--text-color)' }}
          >
            &larr; <span className="hidden sm:inline">ย้อนกลับ</span>
            <span className="sm:hidden">กลับ</span>
          </button>

          <h2 className="text-lg font-bold" style={{ color: 'var(--text-color)' }}>
            การจัดสรรพอร์ต (Slices)
          </h2>
        </div>

        {/* ฝั่งขวา (ปุ่มแก้ไขสัดส่วนและเป้าหมายรวม) */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">

          {displayMode === 'V2' && (
            <div className="flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-center gap-2 sm:gap-1.5 bg-black/20 p-2 sm:p-3 rounded-lg border border-[var(--border-item-light)] w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--text-color)' }}>รวมเงินสดในพอร์ต</span>
                <ToggleSwitch
                  isOn={includeCashInRatio}
                  onToggle={() => setIncludeCashInRatio(prev => !prev)}
                />
              </div>
              <span className="text-[10px] sm:text-xs" style={{ color: 'var(--secondary-text-color)' }}>
                การคำนวณเงินสดเข้ากับสัดส่วน
              </span>
            </div>
          )}

      <div className="flex items-center justify-between gap-3 w-full sm:w-auto bg-black/10 p-2 sm:p-0 rounded-lg sm:bg-transparent">
      {!isPortAdmin && (
        <button
          onClick={onOpenAllocation}
          className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 md:px-2 md:py-1 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded text-yellow-400 text-xs transition-colors shadow-sm"
        >
          <svg className="w-4 h-4 md:w-3 md:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>
          แก้ไขสัดส่วน
        </button>
      )}
      <div className="text-sm font-semibold whitespace-nowrap text-right flex-1 sm:flex-none" style={{ color: 'var(--text-color)' }}>
        เป้าหมายรวม: <br className="block sm:hidden" /> {totalRatio.toFixed(1)}% / 100%
      </div>
    </div>
        </div>
      </div>
      <div className="mt-4 border-t border-gray-600 py-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm" style={{ color: 'var(--text-color)' }}>ความคืบหน้า</span>
          <span className="text-sm" style={{ color: 'var(--text-color)' }}>{totalRatio.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-600 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${totalRatio <= 100 ? 'bg-green-500' : 'bg-red-500'}`}
            style={{ width: `${Math.min(totalRatio, 100)}%` }}
          ></div>
        </div>
      </div>
      <div className="flex-1 space-y-3 mt-2">
        {portfolioArray.length === 0 ? (
          <div className="text-gray-400 text-center py-8">ยังไม่มีพอร์ตย่อย</div>
        ) : (
          portfolioArray?.map((portfolio: any, index: number) => {
            const value = getPortfolioValue(portfolio, currentPrices);
            const currentRatio = ratioBaseValue > 0 ? (value / ratioBaseValue) * 100 : 0;

            return (
              <PortfolioItem
                key={portfolio.subPortId || index}
                portfolio={{
                  ...portfolio,
                  value,
                  stockMetadata: { ...portfolio.stockMetadata, currentRatio: currentRatio || 0 },
                }}
                parentPortId={ids}
                onSelect={() => onSelectPortfolio(portfolio)}
                onEdit={() => onEdit(portfolio)}
                isPortAdmin={isPortAdmin}
                currency={currency}
                currentPrices={currentPrices}
                onRefresh={onRefresh}
                isCrypto={isCrypto}
              />
            );
          })
        )}
        <div
          className="group rounded-xl px-5 py-5 flex flex-col md:flex-row items-stretch md:items-center justify-between cursor-pointer w-full transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-green-900/60 via-green-900/20 to-black/40 border border-green-500/30 hover:border-green-400 hover:shadow-[0_0_25px_rgba(34,197,94,0.5)] backdrop-blur-sm"
          style={{
            // ใช้ var(--green-card-color) เป็นพื้นหลังหลัก
            backgroundColor: 'var(--green-card-color)',
            // หากต้องการให้เป็น gradient โดยใช้ตัวแปรสีนั้น คุณอาจต้องกำหนดไล่ระดับสีผ่าน backgroundImage
            backgroundImage: 'linear-gradient(135deg, var(--green-card-color), rgba(0, 0, 0, 0.5))',
            borderColor: 'var(--green-border-color, rgba(34, 197, 94, 0.3))'
          }}
          onClick={(e) => { e.stopPropagation(); if (onOpenManageCash && !isPortAdmin) onOpenManageCash(); }}
        >
          <div className="flex flex-col md:w-1/4 mb-3 md:mb-0">
            <div className="font-bold text-base md:text-base truncate tracking-wide" style={{ color: 'var(--text-color)' }}>เงินสด</div>
            <div className="text-xs md:text-sm font-medium mt-0.5" style={{ color: 'var(--green-text-color)' }}>Cash Balance</div>
          </div>
          <div className="flex flex-col items-start md:w-1/4 mb-3 md:mb-0 border-t border-gray-700/30 pt-3 md:pt-0 md:border-none">
            <span className="text-xl md:text-lg font-black bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-emerald-300 drop-shadow-sm" style={{ color: 'var(--text-color)' }}>
              {currency === 'THB' ? '฿' : '$'}
              {cashAmountConverted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            {displayMode === 'V1' && (
              <div
                className="text-[10px] md:text-xs font-bold mt-1"
                style={{
                  color: cashDifference >= 0 ? 'var(--green-text-color)' : 'var(--red-text-color)'
                }}
              >
                {cashExpectedRatio > 0 ? (
                  cashDifference >= 0
                    ? `ต้องฝากเพิ่ม: ${currency === 'THB' ? '฿' : '$'}${cashDifferenceConverted.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                    : `มีเงินสดเกิน: ${currency === 'THB' ? '฿' : '$'}${Math.abs(cashDifferenceConverted).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                ) : (
                  <span className="text-red-500 font-bold">กรุณากำหนดเป้าหมายเงินสด</span>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end md:w-1/4 mb-3 md:mb-0 border-t border-gray-700/30 pt-3 md:pt-0 md:border-none pr-0 md:pr-4">
            <div className="flex flex-col items-end">
              <span className="text-lg md:text-base font-bold text-white leading-tight" style={{ color: 'var(--text-color)' }}>{includeCashInRatio ? cashActualRatio.toFixed(1) : '0.0'}%</span>
              <span className="text-[10px] md:text-xs font-bold text-gray-400 mt-0.5"
                style={{
                  color: getProgressBarColor(progressPercentage, cashExpectedRatio),
                  textShadow: `0 0 10px ${getProgressBarColor(progressPercentage, cashExpectedRatio)}88`
                }}
              >เป้าหมาย: {cashExpectedRatio}%</span>
            </div>
            {/* Progress Bar สำหรับเงินสด */}
            <div
              className="h-2 w-32 rounded-full overflow-hidden mt-2 relative border border-gray-700/30"
              style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)' }}
            >
              <div
                className="h-full transition-all duration-700 ease-out absolute right-0 rounded-full"
                style={{
                  width: `${progressBarWidth}%`,
                  background: `linear-gradient(to left, ${getProgressBarColor(progressPercentage, cashExpectedRatio)}, ${getProgressBarColor(progressPercentage, cashExpectedRatio)}CC)`,
                  boxShadow: `0 0 12px ${getProgressBarColor(progressPercentage, cashExpectedRatio)}88`,
                }}
              >
                <div className="absolute top-0 left-0 w-full h-[30%] bg-white/20" />
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end md:w-1/4 gap-4 border-t border-gray-700/30 pt-3 md:pt-0 md:border-none">
            {!isPortAdmin && (
              <button
                className="bg-green-500/10 text-green-400 border border-green-500/40 hover:bg-green-500 hover:text-white hover:border-green-400 hover:shadow-[0_0_15px_rgba(34,197,94,0.4)] px-4 py-2 rounded-lg text-sm font-bold transition-all duration-300 w-full md:w-auto"
                style={{ color: 'var(--text-color)' }}
                onClick={(e) => { e.stopPropagation(); if (onOpenManageCash) onOpenManageCash(); }}
              >
                + จัดการเงินสด
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// 2. Component หน้าหลัก (PortfolioDetailPage)
// ---------------------------------------------------------------------------
const PortfolioDetailPage = ({ selectedSymbol, setSelectedSymbol }: any) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminView = location.pathname.includes('/admin');

  const [isMoveHoldingModalOpen, setIsMoveHoldingModalOpen] = useState(false);
  const [stockToMove, setStockToMove] = useState<any>(null);

  const [activeTab, setActiveTab] = useState<'portfolio' | 'benchmark'>('portfolio');
  const [currency, setCurrency] = useState<'USD' | 'THB'>('USD');
  const STORAGE_KEY_SUBPORT = `selected_subport_${id}`;
  const [isNeedsFixModalOpen, setIsNeedsFixModalOpen] = useState(false);
  // 🟢 เพิ่ม State สำหรับเก็บค่าตั้งต้นที่จะส่งไปให้ ManageCashModal
  const [cashModalInitialDate, setCashModalInitialDate] = useState<string>('');
  const [cashModalInitialAmount, setCashModalInitialAmount] = useState<number>(0);
  const [editingTxnId, setEditingTxnId] = useState<string | null>(null);
  const [pendingEditTxn, setPendingEditTxn] = useState<any | null>(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState<any | null>(() => {
    // ลองเช็คใน localStorage ว่าเคยเลือกพอร์ตย่อยไหนไว้ไหม (จะเช็คตอน mappedSubPorts โหลดเสร็จอีกที)
    return null;
  });
  const [isAddSubPortModalOpen, setIsAddSubPortModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [portfolioName, setPortfolioName] = useState<string>('');
  const [mappedSubPorts, setMappedSubPorts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingNav, setPendingNav] = useState<{ search: string; status: string; type?: string } | null>(null);  // --- Hooks V2 ---
  const { currentPrices, fetchPricesForSymbols, isLoadingPrices } = useMarketPrices();
  const { exchangeRates } = useExchangeRates();
  // 🎯 ดึง userPortfolios, adminPortfolios และ loading state มาจาก Context โดยตรง
  const { displayMode, openTransactionModal, globalRefreshCount, refreshUserPortfolios, userPortfolios, adminPortfolios, loading: isContextLoading } = usePortfolio();
  const [isSellBuyModalOpen, setIsSellBuyModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const [editSubPortData, setEditSubPortData] = useState<any | null>(null);
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
  const [fullPortfolioData, setFullPortfolioData] = useState<any | null>(null);
  const [isManageCashModalOpen, setIsManageCashModalOpen] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState<'holdings' | 'benchmark' | 'transactions'>('holdings');
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [editStock, setEditStock] = useState<any | null>(null);
  const [isConfigStockPopupOpen, setIsConfigStockPopupOpen] = useState(false);
  const [isHoldingAllocationOpen, setIsHoldingAllocationOpen] = useState(false);

  const prevDisplayMode = useRef(displayMode);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  // ✅ โหลดพอร์ตย่อยเดิมจาก Storage ทันทีที่ mappedSubPorts โหลดข้อมูลเสร็จ
  useEffect(() => {
    if (mappedSubPorts.length > 0 && !selectedPortfolio) {
      const savedSubPortId = localStorage.getItem(STORAGE_KEY_SUBPORT);
      if (savedSubPortId) {
        const foundSub = mappedSubPorts.find((sub: any) => sub.subPortId === savedSubPortId);
        if (foundSub) {
          setSelectedPortfolio(foundSub);
        }
      }
    }
  }, [mappedSubPorts, selectedPortfolio]);
  // 2. ปรับ useEffect ให้ Redirect
  useEffect(() => {
    // ตรวจสอบว่า displayMode เปลี่ยนไปจากเดิมหรือไม่
    if (prevDisplayMode.current !== displayMode) {
      prevDisplayMode.current = displayMode; // อัปเดตค่า ref
      navigate('/portfolio'); // Redirect ไปหน้าหลัก
    }
  }, [displayMode, navigate]);

  const handleDeleteStock = async (symbol: string) => {
    // เช็คให้ชัวร์ว่าเรามีข้อมูล subPortId แล้ว
    if (!selectedPortfolio || !selectedPortfolio.subPortId) {
      toast.error('ไม่พบข้อมูลพอร์ตย่อย');
      return;
    }

    try {
      // ใช้ endpoint ที่คุณต้องการ
      await deleteStockFromSubPortfolioTwo(id!, selectedPortfolio.subPortId, symbol);
      toast.success('ลบหุ้นสำเร็จ!');

      // โหลดข้อมูลใหม่
      await refreshUserPortfolios();
      // อัปเดต state selectedPortfolio ด้วยข้อมูลที่อัปเดตล่าสุด

    } catch (error: any) {
      console.error('Error deleting stock:', error);
      toast.error('เกิดข้อผิดพลาดในการลบหุ้น');
    }
  };

  const canRequestRebuild = useMemo(() => {
    if (!fullPortfolioData?.snapshotState) return false;
    const status = fullPortfolioData.snapshotState.status;
    const reconciliationRequired = fullPortfolioData.snapshotState.reconciliationRequired;
    return (
      status === 'REBUILD_REQUIRED' ||
      status === 'REBUILD_FAILED' ||
      status === 'ERROR' ||
      reconciliationRequired === true
    );
  }, [fullPortfolioData]);


  const handleAddClickStock = () => {
    setEditStock(null); // ล้างค่าเผื่อเป็นโหมดเพิ่มใหม่
    setIsStockModalOpen(true); // เปิด Modal
  };

  // 📩 แจ้งปัญหา "มูลค่าพอร์ตไม่ตรง" ผ่านอีเมล (เปิด Gmail แบบเดียวกับ ReportIssueButton)
  const handleReportValueMismatch = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const newWindow = window.open('', '_blank');

    const email = 'Support@wethaiinvest.com';
    const subject = encodeURIComponent('แจ้งปัญหามูลค่าพอร์ตไม่ตรง');
    const reportedAt = new Date().toLocaleString('th-TH', { timeZone: 'Asia/Bangkok' });
    const body = encodeURIComponent(
      `⚠️ แจ้งปัญหา: มูลค่าพอร์ตไม่ตรง ต้องการ Rebuild\n` +

      `ข้อมูลเพิ่มเติมจากระบบ:\n` +
      `Job ID: REBUILD\n` +
      `ID พอร์ต: ${id}\n` +
      `ชื่อพอร์ต: ${portfolioName}\n` +
      `เวลาที่แจ้ง: ${reportedAt}`
    );

    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${subject}&body=${body}`;

    if (newWindow) {
      newWindow.location.href = gmailUrl;
    } else {
      alert('กรุณาอนุญาตให้เว็บไซต์เปิด Pop-up เพื่อแจ้งปัญหาทางอีเมล');
    }
  };

  // 🛠️ ฟังก์ชันจัดการเมื่อคลิกปุ่ม "ไปที่รายการนี้" ให้สลับไปแท็บประวัติรายการทันที
  const handleJumpToTransaction = (transactionId: string) => {
    setRightPanelTab('transactions');
  };
  // 2. สร้างฟังก์ชันบันทึกเงินสดแบบพิเศษ
  const handleSaveCashAndPatch = async () => {
    // สั่งรีเฟรชข้อมูลพอร์ตหลักก่อน
    await handleGlobalRefresh();

    // ถ้ามีการระบุว่ากำลังแก้ Transaction ที่ค้างอยู่ (มาจาก NEEDS_FIX Modal)
    if (editingTxnId) {
      try {
        const currentTimestamp = new Date().toLocaleString('th-TH', {
          timeZone: 'Asia/Bangkok'
        });
        // เรียก Patch Transaction นั้นอีกครั้ง
        // หมายเหตุ: คุณอาจจะต้องดึงข้อมูล Payload เดิมที่เคยพยายาม Patch แล้วพลาดออกมา
        // หรือเรียกฟังก์ชันที่เกี่ยวข้องกับ patchV2Transaction ใหม่อีกครั้งที่นี่
        await patchV2Transaction(id!, editingTxnId, {
          note: `Re-validated at ${currentTimestamp}`
        });
        toast.success('แก้ไขรายการสำเร็จหลังเติมเงิน');
      } catch (err) {
        toast.error('ไม่สามารถ Patch รายการได้หลังเติมเงิน');
      } finally {
        setEditingTxnId(null); // ล้างค่าหลังทำเสร็จ
      }
    }
  };
  // ✅ สร้างฟังก์ชันกลางเพื่อสั่งรีเฟรชทั้งหน้าจอและ Context พร้อมกัน
  const handleGlobalRefresh = async () => {
    setRefreshTrigger(prev => prev + 1);
    if (!isAdminView) {
      await refreshUserPortfolios();
    }
  };
  // ฟังคำสั่งจาก Context หาก Modal อื่นๆ ทำรายการเสร็จ ให้โหลดหน้าต่างนี้ใหม่
  useEffect(() => {
    if (globalRefreshCount > 0) {
      setRefreshTrigger(prev => prev + 1);
    }
  }, [globalRefreshCount]);

  useEffect(() => {
    if (selectedPortfolio && mappedSubPorts.length > 0) {
      const updatedSelectedPort = mappedSubPorts.find((sub: any) => sub.subPortId === selectedPortfolio.subPortId);
      if (updatedSelectedPort && JSON.stringify(updatedSelectedPort) !== JSON.stringify(selectedPortfolio)) {
        setSelectedPortfolio(updatedSelectedPort);
      }
    }
  }, [mappedSubPorts, selectedPortfolio]);

  // 🎯 ดึงข้อมูลพอร์ตแบบ Zero Fetching (เอาจาก Context โดยตรง)
  useEffect(() => {
    let isMounted = true; // Flag สำหรับเช็คว่า Component ยังคงทำงานอยู่

    const fetchDetail = async () => {
      if (!id) return;
      if (isContextLoading) return; // รอให้ Context โหลดเสร็จก่อน

      if (isMounted) setIsLoading(true);

      try {
        // 1. หยิบข้อมูลจาก Context
        let portData = isAdminView
          ? adminPortfolios.find((p: any) => (p.portfolioId || p.portId || p.id) === id)
          : userPortfolios.find((p: any) => (p.portfolioId || p.portId || p.id) === id);

        // 2. Fallback กรณีหาใน Context ไม่เจอ
        if (!portData) {
          const res = isAdminView
            ? await getPublicV2PortfolioDetail(id)
            : await getV2PortfolioDetail(id);
          portData = res?.data?.portfolio || res?.portfolio || res?.data || res;
          portData = normalizeCryptoPortfolio(portData);
        }

        if (!portData) throw new Error("Portfolio not found");

        // 3. ปรับสถานะและ Format ข้อมูล
        if (isMounted) {
          setPortfolioName(portData.name || 'Portfolio Detail');
          setFullPortfolioData(portData);

          let rawSubPorts = portData.subPortfolios || portData.subPorts || [];
          if (rawSubPorts.length === 0 && (portData.holdings?.length > 0 || portData.stocks?.length > 0)) {
            rawSubPorts = [portData];
          }

          const formattedSubPorts = rawSubPorts?.map((sub: any, index: number) => ({
            ...sub,
            subPortId: sub.subPortId || sub.id || `sub-${index}`,
            name: sub.name || portData.name || 'Main Subport',
            stocks: (sub.holdings || sub.stocks || [])?.map((h: any) => ({
              ...h,
              stockSymbol: h.stockSymbol || h.assetSymbol || h.symbol,
              avgBuyPrice: h.avgEntryPrice || h.avgBuyPrice || 0,
            }))
          }));

          setMappedSubPorts(formattedSubPorts);

          // หา symbol ทั้งหมดเพื่อดึงราคา
          const symbols = new Set<string>();
          formattedSubPorts.forEach((sub: any) => {
            sub.stocks.forEach((stock: any) => {
              if (stock.stockSymbol) symbols.add(stock.stockSymbol);
            });
          });
          if (symbols.size > 0) fetchPricesForSymbols(Array.from(symbols));
        }

      } catch (error) {
        console.error("Fetch detail error:", error);
        if (isMounted) toast.error('ไม่สามารถโหลดข้อมูลพอร์ตได้');
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchDetail();

    // Cleanup function: ทำงานเมื่อ Component ถูก Unmount หรือเมื่อ Dependency เปลี่ยน
    return () => {
      isMounted = false;
    };
  }, [id, isContextLoading, userPortfolios, adminPortfolios, displayMode, refreshTrigger, fetchPricesForSymbols, isAdminView]);
  const handleCurrencyToggle = (newCurrency: string) => setCurrency(newCurrency as 'USD' | 'THB');

  // พอร์ตคริปโต: USD เท่านั้น, ไม่มีปันผล (Benchmark เปิดให้ลองใช้แล้ว — error จากหลังบ้านแสดงในกล่อง Benchmark เอง)
  const isCrypto = isCryptoPortfolio(fullPortfolioData);
  useEffect(() => {
    if (!isCrypto) return;
    setCurrency('USD');
  }, [isCrypto]);
  const convertCurrency = (value: number, curr: string) => curr === "THB" ? value * (exchangeRates["THB"] || 1) : value;

  const handleSelectPortfolio = (portfolio: any) => {
    setSelectedPortfolio(portfolio);
    // ✅ บันทึก subPortId ลง Storage
    if (portfolio?.subPortId) {
      localStorage.setItem(STORAGE_KEY_SUBPORT, portfolio.subPortId);
    }
  };
  const handleBackToPortfolioSelection = () => {
    setSelectedPortfolio(null);
    // ✅ ลบค่าออกจาก Storage เมื่อกดกลับมาหน้าพอร์ตหลัก
    localStorage.removeItem(STORAGE_KEY_SUBPORT);
  };

  const handleEditSubPort = (portfolio: any) => {
    setEditSubPortData(portfolio);
    setIsAddSubPortModalOpen(true);
  };

  const handleAddSubPort = () => {
    setEditSubPortData(null);
    setIsAddSubPortModalOpen(true);
  };
  const handleSaveStockRatios = async (
    portfolioId: string,
    updatedRatios: { id: string; ratio: number }[]
  ) => {
    try {
      if (!selectedPortfolio || !selectedPortfolio.subPortId) {
        toast.error('Selected portfolio or sub-portfolio ID is null or undefined');
        return;
      }

      setIsLoadingStock(true); // เริ่มการโหลดข้อมูล
      const failedUpdates: {}[] = [];

      // Update stock ratios
      for (const { id, ratio } of updatedRatios) {
        const stock = selectedPortfolio.stocks.find(
          (s: any) => s.id === id || s.stockSymbol === id || s.symbol === id
        );

        if (!stock) {
          console.warn(`Stock with ID ${id} not found in selected portfolio`);
          continue;
        }

        try {
          await updateStockInSubPortfolioTwo(
            portfolioId,
            selectedPortfolio.subPortId,
            stock.stockSymbol || stock.symbol,
            {
              stockSymbol: stock.stockSymbol || stock.symbol,
              quantity: stock.quantity,
              avgBuyPrice: stock.avgBuyPrice,
              expectedRatio: ratio,
            }
          );
        } catch (error) {
          console.error(`Error updating stock ${stock.stockSymbol || stock.symbol}:`, error);
          // toast.error(`Error updating stock ${stock.stockSymbol || stock.symbol}`);
          failedUpdates.push({ stock });
          // toast.error(`Failed to update stock with ID ${JSON.stringify(failedUpdates)}`);
        }
      }

      // Reload updated portfolio data
      const updatedPortfolio = await getPortfolioDetailTwo(portfolioId);

      const updatedSubPortfolio = updatedPortfolio.subPorts.find(
        (p: any) => p.subPortId === selectedPortfolio.subPortId
      );

      if (updatedSubPortfolio) {
        setSelectedPortfolio(updatedSubPortfolio);

        // คงค่า editStock เดิมไว้
        const currentStock = updatedSubPortfolio.stocks.find(
          (stock: any) => stock.stockSymbol === editStock?.stockSymbol
        );
        if (currentStock) {
          setEditStock(currentStock);
        } else {
          setEditStock(null); // หากไม่พบหุ้นเดิมในลิสต์ ให้ล้างค่า
        }
      }

      if (failedUpdates.length > 0) {
        toast.error(`Failed to update ${JSON.stringify(failedUpdates)} stocks.`);
      }

      await refreshUserPortfolios();

      setIsLoadingStock(false);
      setIsConfigStockPopupOpen(false);
      toast.success('อัปเดตสัดส่วนสำเร็จ');
      // toast.success('Stock ratios updated successfully');
    } catch (error) {
      console.error('Error updating stock ratios:', error);
      setIsLoadingStock(false); // การโหลดเสร็จสิ้นแม้เกิดข้อผิดพลาด
      toast.error(JSON.stringify(error));
    }
  };
  // =======================================================================
  // 🎯 การคำนวณ Hybrid Real-time: นิ่งจาก Summary + ดิ้นได้จาก MarketPrice
  // =======================================================================
  const cashValue = num(fullPortfolioData?.summary?.cashValue ?? fullPortfolioData?.cashBalances?.[0]?.amount);
  const totalCost = num(fullPortfolioData?.summary?.assetCostValue);

  const realTimeAssetValue = useMemo(() => {
    if (!fullPortfolioData?.subPorts && !mappedSubPorts) return 0;
    const portsToCalculate = mappedSubPorts.length > 0 ? mappedSubPorts : (fullPortfolioData?.subPorts || []);
    let totalAsset = 0;

    portsToCalculate.forEach((subPort: any) => {
      const items = subPort.holdings || subPort.stocks || [];
      items.forEach((h: any) => {
        const symbol = h.stockSymbol || h.assetSymbol || h.symbol;
        // ใช้ราคาจาก currentPrices ถ้าไม่มีให้ใช้ทุนเดิม
        const currentPrice = currentPrices[symbol] ?? num(h.avgEntryPrice ?? h.avgBuyPrice);
        totalAsset += num(h.quantity) * currentPrice;
      });
    });
    return totalAsset;
  }, [fullPortfolioData, mappedSubPorts, currentPrices]);

  const realTimeNetValue = realTimeAssetValue + cashValue;
  const realTimeUnrealizedProfit = realTimeAssetValue - totalCost;
  const realTimeProfitPercent = totalCost > 0 ? (realTimeUnrealizedProfit / totalCost) * 100 : 0;
  // =======================================================================
  const calculateRemaining = (portfolio: any) => {
    // ดึง subPorts ออกมา
    const subPorts = portfolio.subPorts || [];

    // หาผลรวมของ expectedRatio
    const totalSubPortRatio = subPorts.reduce((sum: any, sp: any) => {
      return sum + (sp.expectedRatio || 0);
    }, 0);

    // คำนวณส่วนที่เหลือ (ตรวจสอบไม่ให้ติดลบ)
    return Math.max(100 - totalSubPortRatio, 0);
  };
  if (isLoading || isContextLoading) {
    return <div className="text-center mt-20 text-gray-400">กำลังโหลดข้อมูลพอร์ต...</div>;
  }

  return (
    <div className="p-4">


      {activeTab === 'portfolio' ? (
        <div className="p-2 animate-fadeIn">
          <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-2 gap-4">

            <div className="flex flex-col w-full md:w-auto">
              <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                <h1 className="text-2xl md:text-4xl font-bold truncate" style={{ color: 'var(--text-color)' }}>
                  {portfolioName}
                </h1>
                {isAdminView && (
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-xs font-bold border border-yellow-500/50">
                    พอร์ตแอดมิน
                  </span>
                )}
              </div>

              {/* ⚠️ แจ้งปัญหา "มูลค่าพอร์ตไม่ตรง" (แสดงเฉพาะจอ Desktop และโหมด V2) */}
              {!isAdminView && displayMode === 'V2' && (
                <button
                  type="button"
                  onClick={handleReportValueMismatch}
                  className="hidden md:inline-flex items-center gap-1.5 w-fit mt-2 px-3 py-1.5 rounded-full border border-gray-700 hover:border-yellow-500/50 bg-black/20 hover:bg-yellow-500/10 text-xs transition-all group"
                >
                  <span className="text-yellow-500/80 group-hover:text-yellow-400">⚠️</span>
                  <span style={{ color: 'var(--secondary-text-color)' }}>มูลค่าพอร์ตไม่ตรงหลังจัดการพอร์ต?</span>
                  <span className="text-yellow-400 group-hover:text-yellow-300 font-semibold underline underline-offset-2">รายงาน</span>
                </button>
              )}
            </div>
            <div className="flex flex-row items-center gap-3 w-full md:w-auto">
              <div className="grid grid-cols-2 md:flex gap-3 w-full md:w-auto">
                {/* <button
                  onClick={() => navigate(isAdminView ? '/admin' : '/portfolio')}
                  className="flex items-center justify-center gap-2 px-4 py-3 rounded font-medium transition-all w-full md:w-auto md:py-2 min-h-[50px] md:min-h-0 hover:bg-gray-700"
                  style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--text-color)' }}
                >
                  <span>&larr;</span> ย้อนกลับ
                </button> */}
                {/* 1. เพิ่มปุ่มย้อนกลับมาไว้ตรงนี้ */}
                <button
                  onClick={() => navigate(isAdminView ? '/admin' : '/portfolio')}
                  className="md:hidden flex items-center justify-center gap-2 px-4 py-2 rounded font-medium transition-all w-full md:w-auto min-h-[50px] md:min-h-0 hover:bg-gray-700"
                  style={{ backgroundColor: 'var(--button-bg-color)', color: 'var(--text-color)' }}
                >
                  <span>&larr;</span> กลับ
                </button>
                {/* ✅ 2. ปุ่ม Rebuild (แสดงเฉพาะตอนพอร์ตป่วย) */}
                {canRequestRebuild && displayMode === 'V2' && (
                  <button
                    onClick={async () => {
                      if (!window.confirm("ต้องการซ่อมพอร์ต (Rebuild Snapshot) ใช่หรือไม่?")) return;

                      setIsLoading(true); // ถ้ามี state นี้
                      try {
                        await requestRebuildPortfolio(id!);
                        toast.success('ส่งคำสั่ง Rebuild สำเร็จ');
                        handleGlobalRefresh();
                      } catch (e: any) {
                        // ดึงข้อความ error จากที่ส่งมา
                        const errMsg = e.response?.data?.message || 'ไม่สามารถ Rebuild ได้';
                        toast.error(errMsg);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    disabled={isLoading}
                    className="bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white font-bold px-4 py-2 rounded shadow transition-all w-full md:w-auto md:py-2 text-sm disabled:opacity-50"
                  >
                    {isLoading ? 'กำลังประมวลผล...' : '⚠️ ซ่อมพอร์ต (Rebuild)'}
                  </button>
                )}


                {/* ⚠️ แสดงปุ่ม/กล่องแจ้งเตือน "รายการที่ต้องแก้" เฉพาะเมื่อเลือกพอร์ตย่อยแล้ว และพอร์ตมีสถานะ NEEDS_FIX */}
                {/* ⚠️ แสดงปุ่ม "รายการที่ต้องแก้" เฉพาะเมื่อเลือกพอร์ตย่อยแล้ว และพอร์ตมีสถานะ NEEDS_FIX */}
                {displayMode === 'V2' && selectedPortfolio && fullPortfolioData?.snapshotState?.chainStatus === 'NEEDS_FIX' && (
                  <div className="flex items-center">
                    <button
                      onClick={() => setIsNeedsFixModalOpen(true)}
                      className="py-2.5 px-4 bg-yellow-500/20 border border-yellow-500/50 hover:bg-yellow-500/30 text-yellow-300 rounded-xl font-bold flex items-center justify-between gap-3 shadow-lg transition-all animate-pulse h-[46px]"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">⚠️</span>
                        <span className="text-xs whitespace-nowrap">มีรายการต้องแก้ไข</span>
                      </div>
                      {/* <span className="bg-yellow-500 text-black text-[11px] px-2.5 py-1 rounded-lg font-black whitespace-nowrap">
                        เปิดดู &rarr;
                      </span> */}
                    </button>
                  </div>
                )}

                {/* พอร์ตคริปโตใช้ CryptoCSVImportModal (รูปแบบ CRYPTO_USD) */}
                {!isAdminView && displayMode === 'V2' && selectedPortfolio && (
                  <div className="relative inline-block mt-2">

                    {/* Tag BETA ที่ลอยอยู่ด้านนอกขวาบน */}
                    <span className="absolute -top-3 -right-3 bg-blue-600 text-white text-[9px] px-2 py-0.5 rounded-full font-bold shadow-md z-10 animate-pulse border border-white/20">
                      BETA
                    </span>

                    <button
                      onClick={() => setIsImportModalOpen(true)}
                      className="bg-yellow-400 text-[#18181b] hover:bg-yellow-500 font-bold px-6 py-4 rounded shadow-lg transition-all w-full md:w-auto min-h-[50px] md:min-h-0 text-sm md:text-base"
                    >
                      Import CSV
                    </button>
                  </div>

                )}
                {!isAdminView && (
                  // 1. ถ้ายังไม่ได้เลือกพอร์ตย่อย (อยู่หน้าพอร์ตหลัก) -> โชว์ปุ่มสร้างพอร์ตย่อย
                  !selectedPortfolio ? (
                    <button
                      onClick={handleAddSubPort}
                      className="bg-yellow-400 text-[#18181b] hover:bg-yellow-500 font-bold px-4 py-2 rounded shadow-lg transition-all w-full md:w-auto md:py-2 min-h-[50px] md:min-h-0 text-sm md:text-base"
                    >
                      + สร้างพอร์ตย่อย
                    </button>
                  ) : (
                    // 2. ถ้าเลือกพอร์ตย่อยแล้ว -> โชว์ปุ่มตาม displayMode
                    displayMode === 'V1' ? (
                      <button
                        onClick={handleAddClickStock}
                        className="bg-yellow-400 text-[#18181b] hover:bg-yellow-500 font-bold px-4 py-2 rounded shadow-lg transition-all w-full md:w-auto md:py-2 min-h-[50px] md:min-h-0 text-sm md:text-base"
                      >
                        + เพิ่มหุ้น
                      </button>
                    ) : (
                      <button
                        onClick={() => openTransactionModal({ portId: id, subPortId: selectedPortfolio?.subPortId })}
                        className="bg-yellow-400 text-[#18181b] hover:bg-yellow-500 font-bold px-4 py-2 rounded shadow-lg transition-all w-full md:w-auto md:py-2 min-h-[50px] md:min-h-0 text-sm md:text-base"
                      >
                        {isCrypto ? '+ ซื้อขายเหรียญ' : '+ ซื้อขายหุ้น'}
                      </button>
                    )
                  )
                )}

                {!isCrypto && (
                  <div className="col-span-2 md:col-span-1 w-full md:w-auto bg-[var(--component-bg-color)] p-1 rounded-lg border border-[var(--text-input)] flex items-center justify-center min-h-[50px] md:min-h-0">
                    <ToggleCurrency selectedCurrency={currency} onToggle={() => handleCurrencyToggle(currency === 'USD' ? 'THB' : 'USD')} />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-6 mt-4">

            <style>{`
              @keyframes fadeSlideLeft {
                0% { opacity: 0; transform: translateX(-40px); }
                100% { opacity: 1; transform: translateX(0); }
              }
              .animate-fade-slide-left {
                animation: fadeSlideLeft 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
              }
            `}</style>

            {/* 1. แสดงกราฟด้านซ้าย เฉพาะเมื่ออยู่หน้า 'holdings' (หุ้นในพอร์ต) เท่านั้น */}
            {rightPanelTab === 'holdings' && (
              <div className="lg:col-span-2 col-span-1 animate-fade-slide-left">
                <div className="h-full" style={{ backgroundColor: 'var(--component-bg-color)' }}>
                  {selectedPortfolio ? (
                    <PortfolioSingleChartBox isAdminPort={isAdminView} portfolios={selectedPortfolio} currentPrices={currentPrices} currency={currency} convertCurrency={convertCurrency} setIsConfigPopupOpen={() => { }} isCrypto={isCrypto} />
                  ) : (
                    <PortfolioChartBox fromAdmin={isAdminView} cashAmount={cashValue} portfolios={mappedSubPorts} currentPrices={currentPrices} currency={currency} isCrypto={isCrypto} />
                  )}
                </div>
              </div>
            )}

            {/* 2. ขยายพื้นที่ฝั่งขวาเต็ม 6 คอลัมน์ ถ้าดูกราฟ Benchmark หรือ ประวัติรายการ */}
            <div className={`${rightPanelTab !== 'holdings' ? 'lg:col-span-6' : 'lg:col-span-4'} col-span-1 flex flex-col transition-all duration-300`}>

              {/* 🌟 แสดง Tabs (V1 จะเห็น 2 แท็บ, V2 จะเห็นครบ 3 แท็บ) */}
              <div
                className="flex gap-2 bg-black/20 p-1.5 rounded-xl shadow-inner mb-4"
                style={{ borderColor: 'var(--border-item-light)' }}
              >
                <button
                  onClick={() => { setRightPanelTab('holdings'); setPendingNav(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 px-2 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 ${rightPanelTab === 'holdings'
                    ? 'bg-yellow-400 text-black shadow-md scale-100'
                    : 'text-gray-500 hover:text-white hover:bg-white/5 scale-[0.98]'
                    }`}
                >
                  <span className="text-base">📊</span> {isCrypto ? 'เหรียญในพอร์ต' : 'หุ้นในพอร์ต'}
                </button>

               <button
                  onClick={() => { setRightPanelTab('benchmark'); setPendingNav(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 px-2 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 ${rightPanelTab === 'benchmark'
                    ? 'bg-yellow-400 text-black shadow-md scale-100'
                    : 'text-gray-500 hover:text-white hover:bg-white/5 scale-[0.98]'
                    }`}
                >
                  <span className="text-base">📈</span> เปรียบเทียบ Benchmark
                </button>

                {displayMode === 'V2' && !isAdminView && (
                  <button
                    onClick={() => { setRightPanelTab('transactions'); setPendingNav(null); }}
                    className={`flex-1 flex items-center justify-center gap-2 px-2 py-2.5 rounded-lg text-sm font-bold transition-all duration-300 ${rightPanelTab === 'transactions'
                      ? 'bg-yellow-400 text-black shadow-md scale-100'
                      : 'text-gray-500 hover:text-white hover:bg-white/5 scale-[0.98]'
                      }`}
                  >
                    <span className="text-base">📝</span> ประวัติรายการ
                  </button>
                )}
              </div>

              <div className="h-full animate-fadeIn">
                {rightPanelTab === 'benchmark' ? (
                  /* 🌟 นำ BenchmarkComponent มาแสดงที่นี่ */
                  <div className="bg-[var(--component-bg-color)] rounded-xl border border-[var(--border-item-light)] overflow-hidden shadow-lg h-full">
                    <BenchmarkComponent
                      portfolios={mappedSubPorts}
                      portfolioId={id!}
                      portfolioName={portfolioName}
                    />
                  </div>
                ) : rightPanelTab === 'transactions' ? (
                  <TransactionHistoryV2
                    portfolioId={id!}
                    subPorts={mappedSubPorts}
                    selectedSubPortId={selectedPortfolio?.subPortId}
                    onAddTransaction={() => openTransactionModal({ portId: id, subPortId: selectedPortfolio?.subPortId })}
                    refreshTrigger={refreshTrigger + globalRefreshCount}
                    onSettleSuccess={handleGlobalRefresh}
                    initialSearch={pendingNav?.search || ''}
                    initialStatus={pendingNav?.status || 'ALL'}
                    initialType={pendingNav?.type || 'ALL'}
                    initialEditTransaction={pendingEditTxn}
                    onClearEditTransaction={() => setPendingEditTxn(null)}
                    affectedTransactionIds={fullPortfolioData?.snapshotState?.affectedTransactions?.map((t: any) => t.transactionId) || []}
                    fullPortfolioData={fullPortfolioData}
                    isCrypto={isCrypto}

                  />
                ) : selectedPortfolio ? (
                  <PortfolioStocksBox
                    portfolio={selectedPortfolio}
                    onBack={handleBackToPortfolioSelection}
                    portfolioId={fullPortfolioData.portfolioId}
                    isPortAdmin={isAdminView}
                    currency={currency}
                    convertCurrency={convertCurrency}
                    currentPrices={currentPrices}
                    onDeleteStock={handleDeleteStock}
                    onEditStock={() => { }}
                    onMoveStock={(stock) => {
                      setStockToMove([stock]);
                      setIsMoveHoldingModalOpen(true);
                    }}
                    onSelectStock={() => { }}
                    onRefresh={handleGlobalRefresh}
                    setIsSellBuyModalOpen={setIsSellBuyModalOpen}
                    setIsImportModalOpen={setIsImportModalOpen}
                    isCrypto={isCrypto}
                    onNavigateToPendingDividend={isCrypto ? undefined : (symbol: string) => {
                      setRightPanelTab('transactions');
                      setPendingNav({ search: symbol, status: 'PENDING' });
                    }}
                    onNavigateToSettledDividend={isCrypto ? undefined : (symbol: string) => {
                      setRightPanelTab('transactions');
                      setPendingNav({ search: symbol, status: 'SETTLED', type: 'DIVIDEND' });
                    }}
                  />
                ) : (
                  <div className="rounded-b-xl shadow-lg p-4 h-full min-h-[600px] flex flex-col" style={{ backgroundColor: 'var(--component-bg-color)' }}>
                    <PortfolioAllocationBox
                      subPortfolios={mappedSubPorts}
                      currentPrices={currentPrices}
                      onSelectPortfolio={handleSelectPortfolio}
                      onEdit={handleEditSubPort}
                      onOpenAllocation={() => setIsAllocationModalOpen(true)}
                      onOpenManageCash={() => setIsManageCashModalOpen(true)}
                      ids={id!}
                      onRefresh={handleGlobalRefresh}
                      currency={currency}
                      isPortAdmin={isAdminView}
                      fullPortfolio={fullPortfolioData}
                      convertCurrency={convertCurrency}
                      isCrypto={isCrypto}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-2 animate-fadeIn max-w-7xl mx-auto">
          <BenchmarkComponent
            portfolios={mappedSubPorts}
            portfolioId={id!}
            portfolioName={portfolioName}
          />
        </div>
      )
      }

      {
        isAddSubPortModalOpen && fullPortfolioData && (
          <AddSubPortV2Modal
            isOpen={isAddSubPortModalOpen}
            onClose={() => { setIsAddSubPortModalOpen(false); setEditSubPortData(null); }}
            portfolioId={id!}
            initialData={editSubPortData}
            onSave={handleGlobalRefresh}
            onOpenAllocation={() => setIsAllocationModalOpen(true)}
            totalRatio={fullPortfolioData.summary?.totalExpectedRatio || 0}
            remainingRatio={fullPortfolioData.remainingExpectedRatio || calculateRemaining(fullPortfolioData)}
            isAllocationOpen={isAllocationModalOpen}
          />
        )
      }

      {
        isAllocationModalOpen && fullPortfolioData && (
          <EditAllocationModal
            isOpen={isAllocationModalOpen}
            onClose={() => { setIsAllocationModalOpen(false); setIsAddSubPortModalOpen(false); }}
            onSave={handleGlobalRefresh}
            portfolio={{ ...fullPortfolioData, subPorts: mappedSubPorts }}
            isSideBySide={isAddSubPortModalOpen}
          />
        )
      }

      {
        isManageCashModalOpen && fullPortfolioData && (
          <ManageCashModal
            isOpen={isManageCashModalOpen}
            onClose={() => {
              setIsManageCashModalOpen(false);
              setCashModalInitialDate('');
              setCashModalInitialAmount(0);
            }}
            onSave={handleGlobalRefresh} // 👈 ใช้ฟังก์ชันใหม่ที่เราสร้างไว้ข้างบน
            portfolioId={id!}
            currentCash={cashValue}
            isCrypto={isCrypto}
            currentCashExact={isCrypto ? String(fullPortfolioData?.summary?.cashValue ?? fullPortfolioData?.cashBalances?.[0]?.amount ?? '0') : undefined}
            initialDate={cashModalInitialDate}       // 👈 ส่งวันที่ -1 วันไป
            initialAmount={cashModalInitialAmount}   // 👈 ส่งยอดเงินที่ขาดไป
          />
        )
      }

      {
        isMoveHoldingModalOpen && selectedPortfolio && (
          <MoveHoldingModal
            isOpen={isMoveHoldingModalOpen}
            onClose={() => { setIsMoveHoldingModalOpen(false); setStockToMove(null); }}
            portfolioId={id!}
            sourceSubPort={selectedPortfolio}
            allSubPorts={mappedSubPorts}
            stocks={stockToMove}
            onSave={handleGlobalRefresh}
          />
        )
      }

      {
        isStockModalOpen && (
          <AddEditStock
            isOpen={isStockModalOpen}
            onClose={() => { setIsStockModalOpen(false); setEditStock(null); }}
            portfolio={selectedPortfolio}
            portfolioId={id!}
            initialData={editStock}
            setIsConfigPopupOpen={setIsHoldingAllocationOpen}
            onRefresh={handleGlobalRefresh}
            onOpenHoldingAllocation={() => setIsHoldingAllocationOpen(true)}
            isAllocationOpen={isHoldingAllocationOpen}
          // remainRatio={calculateRemaining(fullPortfolioData)} // state นี้คุณมีอยู่แล้ว
          />
        )}
      {
        isHoldingAllocationOpen && selectedPortfolio && (
          <EditHoldingAllocationModal
            isOpen={isHoldingAllocationOpen}
            onClose={() => setIsHoldingAllocationOpen(false)} // ปิดตัวขวา
            portfolioId={id!}
            subPortfolio={selectedPortfolio} // ข้อมูลพอร์ตย่อย
            onSave={handleGlobalRefresh}
            isSideBySide={isStockModalOpen} // ✅ ส่งเป็น true ถ้า AddEditStock เปิดอยู่
          />
        )
      }



      {
        isImportModalOpen && isCrypto && (
          <CryptoCSVImportModal
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            portfolioId={id!}
            subPortId={selectedPortfolio?.subPortId}
            subPortName={selectedPortfolio?.name}
            onSave={handleGlobalRefresh}
          />
        )
      }

      {
        isImportModalOpen && !isCrypto && (
          <CSVImportModal
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            portfolioId={id!} // ใช้ ID พอร์ตจาก URL
            subPortId={selectedPortfolio?.subPortId} // ใช้ subPortId ของพอร์ตที่เลือก
            onSave={handleGlobalRefresh}
          />
        )
      }

      {/* 🪟 Modal แสดงรายชื่อ affectedTransactions ทั้งหมด */}
      {isNeedsFixModalOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsNeedsFixModalOpen(false)}></div>
          <div className="relative w-full max-w-lg bg-[#18181b] border border-yellow-500/40 rounded-2xl shadow-2xl p-6 text-white space-y-4 animate-fadeIn">

            {/* Header */}
            <div className="flex justify-between items-center border-b border-gray-800 pb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">⚠️</span>
                <h3 className="text-base font-bold text-yellow-400">รายการที่ต้องแก้ไข (Affected Transactions)</h3>
              </div>
              <button onClick={() => setIsNeedsFixModalOpen(false)} className="text-gray-400 hover:text-white text-2xl font-bold">&times;</button>
            </div>

            {/* ℹ️ คำอธิบายหลักการแก้ไขตามสเปก */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-xs text-yellow-200/90 leading-relaxed">
              <span className="font-bold text-yellow-400">💡 คำแนะนำ:</span> การแก้ไข Transaction ย้อนหลังจะต้องทำตามลำดับเวลาจาก <span className="font-semibold underline">รายการเก่าไปหาใหม่</span> เสมอ เนื่องจากระบบใช้ Transaction Ledger เป็นตัวคำนวณสืบทอดผลลัพธ์ต่อเนื่องกัน
            </div>

            {/* List รายการ */}
            <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
              {fullPortfolioData?.snapshotState?.affectedTransactions?.map((tx: any, idx: number) => (
                <div key={idx} className="bg-gray-900 border border-yellow-500/30 rounded-xl p-4 space-y-3 shadow">

                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2 font-bold text-yellow-300 text-xs">
                      <span className="px-2 py-0.5 bg-yellow-500/20 rounded">{tx.type}</span>
                      <span>{tx.ticker}</span>
                      <span className="text-gray-400 font-normal">({tx.transactionDate?.split('T')[0]})</span>
                    </div>

                    {/* Error Code พร้อมปุ่มดูความหมาย [ℹ️] */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-0.5 rounded font-mono">
                        {tx.errorCode}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          let detail = "ความหมาย Error Code:\n\n";
                          if (tx.errorCode === 'INSUFFICIENT_HOLDING') {
                            detail += "• INSUFFICIENT_HOLDING: หุ้นในพอร์ตไม่เพียงพอสำหรับทำรายการขายหรือโอนออก (ต้องตรวจสอบจำนวนหุ้น หรือรายการซื้อก่อนหน้า)";
                          } else if (tx.errorCode === 'INSUFFICIENT_CASH') {
                            detail += "• INSUFFICIENT_CASH: เงินสดในพอร์ตไม่เพียงพอสำหรับทำรายการซื้อหรือหักค่าธรรมเนียม (ต้องเติมเงินสดย้อนหลัง)";
                          } else {
                            detail += `• ${tx.errorCode}: เกิดข้อผิดพลาดในการตรวจสอบ Chain ของพอร์ต`;
                          }
                          alert(detail);
                        }}
                        className="w-5 h-5 rounded-full bg-blue-500/20 hover:bg-blue-500/40 text-blue-400 text-[10px] font-bold flex items-center justify-center transition-colors cursor-pointer"
                        title="ดูความหมาย Error Code"
                      >
                        ℹ️
                      </button>
                    </div>
                  </div>

                  {/* แสดง Transaction ID แบบคลิกเลือกง่าย */}
                  <div className="text-[10px] font-mono text-gray-400 bg-black/40 px-2 py-1 rounded select-all flex items-center justify-between">
                    <span>ID: {tx.transactionId}</span>
                  </div>

                  <p className="text-xs text-gray-300 leading-relaxed">
                    {tx.th_reason || tx.reason}
                  </p>

                  {/* Action Buttons */}
                  <div className="flex justify-end gap-2 pt-2 border-t border-gray-800">
                    <button
                      onClick={async () => {
                        setIsNeedsFixModalOpen(false);
                        const res = await getV2TransactionById(id!, tx.transactionId);
                        const fullTransactionData = res?.data?.transaction || res?.data || res;
                        setPendingEditTxn(fullTransactionData);
                        setRightPanelTab('transactions');
                      }}
                      className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-gray-950 text-xs font-bold rounded transition-colors shadow"
                    >
                      ⚙️ ไปที่รายการนี้และแก้ไข
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="flex justify-end pt-2 border-t border-gray-800">
              <button
                onClick={() => setIsNeedsFixModalOpen(false)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs font-bold rounded-lg transition-colors"
              >
                ปิดหน้าต่าง
              </button>
            </div>

          </div>
        </div>
      )}
    </div >
  );
};

export default PortfolioDetailPage;